import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import { PriceLabel } from "shared/components/currency/PriceLabel";
import FormatNumber from "shared/components/numbers/FormatNumber";
import FormatQuantity from "shared/components/quantity/FormatQuantity";
import FormatCurrency from "shared/components/currency/FormatCurrency";

import { Header, Button, Grid, Responsive } from "semantic-ui-react";
import { Unless } from "shared/components/elements/Conditions";
import { getSectionPath } from "shared/selectors/unit";
import { getUserRole } from "shared/selectors";
import { get, compact } from "lodash";
import { handleScrollTop } from "shared/helpers/scrollTo";
import { roomBookController } from "shared/actions/roomBookController";
import AdminMessageDialog from "./AdminMessageDialog";
import { withRouter } from "../../helpers/withRouter";
import { ImageCanvas } from "../images/ImageCanvas";

class SubLineItemGrid extends React.Component {
  static propTypes = {
    subLineItems: PropTypes.array.isRequired,
    sectionPath: PropTypes.string.isRequired,
    userRole: PropTypes.string.isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      showAdminMessage: false
    };
  }

  handleClose = () => {
    this.setState({ showAdminMessage: false });
  };

  render() {
    const { subLineItems } = this.props;
    const { showAdminMessage } = this.state;
    return (
      <div>
        <Responsive minWidth={Responsive.onlyTablet.minWidth}>
          {this.renderLargeGrid(subLineItems)}
        </Responsive>
        <Responsive maxWidth={Responsive.onlyMobile.maxWidth}>
          {this.renderMobileGrid(subLineItems)}
        </Responsive>

        <AdminMessageDialog
          showAdminMessage={showAdminMessage}
          handleClose={this.handleClose}
        />
      </div>
    );
  }

  renderLargeGrid = subLineItems => {
    const items = subLineItems.map(subLineItem => {
      const validOptions = subLineItem.options.filter(o => o.isValidOption);
      const selectedProduct =
        subLineItem.defactoSelectedOption || validOptions[0];
      const description = (
        <Header as="h4">
          <Header.Content>
            {selectedProduct.name}
            <Header.Subheader>
              {compact([selectedProduct.supplier, selectedProduct.series]).join(
                ", "
              )}
            </Header.Subheader>
          </Header.Content>
        </Header>
      );

      return (
        <Grid.Row key={subLineItem.id} data-component="subLineItemGrid">
          <Grid.Column width={2} style={{ minHeight: "100px" }}>
            <ImageCanvas src={selectedProduct.thumbUrl} />
          </Grid.Column>
          <Grid.Column width={5} verticalAlign="middle">
            <Header as="h3">{subLineItem.title}</Header>
            {description}
            {this.renderProductInfo(subLineItem, selectedProduct)}
          </Grid.Column>
          <Grid.Column width={2} verticalAlign="middle">
            <Unless condition={subLineItem.isProgressed}>
              <Unless condition={validOptions.length < 2}>
                <Button
                  basic
                  content="Konfigurieren"
                  onClick={() => {
                    this.navigateTo(subLineItem);
                  }}
                />
              </Unless>
            </Unless>
          </Grid.Column>
          <Grid.Column width={3} verticalAlign="middle">
            {this.renderQuantity(subLineItem)}
          </Grid.Column>
          <Grid.Column width={4} verticalAlign="middle" textAlign="right">
            <PriceLabel
              amount={subLineItem.total}
              isOnRequest={subLineItem.isPriceOnRequest}
            />
          </Grid.Column>
        </Grid.Row>
      );
    });

    return <Grid>{items}</Grid>;
  };

  renderMobileGrid = subLineItems => {
    return subLineItems.map(subLineItem => {
      const validOptions = subLineItem.options.filter(o => o.isValidOption);
      const selectedProduct =
        subLineItem.defactoSelectedOption || validOptions[0];

      return (
        <Grid key={subLineItem.id}>
          <Grid.Row>
            <Grid.Column width={16}>
              <Header as="h3">{subLineItem.title}</Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={6}>
              <ImageCanvas src={selectedProduct.thumbUrl} />
            </Grid.Column>
            <Grid.Column width={10} verticalAlign="middle">
              <Header as="h4">
                <Header.Content>
                  {selectedProduct.name}
                  <Header.Subheader>
                    {compact([
                      selectedProduct.supplier,
                      selectedProduct.series
                    ]).join(", ")}
                  </Header.Subheader>
                </Header.Content>
              </Header>
              {this.renderProductInfo(subLineItem, selectedProduct)}
            </Grid.Column>
          </Grid.Row>
          <Unless condition={subLineItem.isProgressed}>
            <Unless condition={validOptions.length < 2}>
              <Grid.Row>
                <Grid.Column width={16}>
                  <Button
                    basic
                    fluid
                    content="Konfigurieren"
                    onClick={() => {
                      this.navigateTo(subLineItem);
                    }}
                  />
                </Grid.Column>
              </Grid.Row>
            </Unless>
          </Unless>
          <Grid.Row>
            <Grid.Column width={8} verticalAlign="middle">
              {this.renderQuantity(subLineItem)}
            </Grid.Column>
            <Grid.Column width={8} verticalAlign="middle" textAlign="right">
              <PriceLabel amount={subLineItem.total} />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      );
    });
  };

  renderProductInfo = (subLineItem, selectedProduct) => {
    if (subLineItem.priceStrategy !== "flat") {
      return (
        <p>
          <small data-attribute="defaultQuantity" className="muted">
            <FormatQuantity
              value={subLineItem.defaultQuantity}
              unit={selectedProduct.priceStrategy}
              mode="compact"
            />{" "}
            laut Baubeschreibung
          </small>
          <br />
          <small data-attribute="excessPrice" className="muted">
            <FormatCurrency
              amount={selectedProduct.excessPrice}
              priceStrategy={subLineItem.priceStrategy}
              isOnRequest={subLineItem.isPriceOnRequest}
            />
          </small>
        </p>
      );
    }
  };

  renderQuantity = subLineItem => {
    return (
      <Unless condition={subLineItem.priceStrategy === "flat"}>
        <Unless condition={subLineItem.isProgressed}>
          <Button
            content="-"
            onClick={this.updateQuantityFn(subLineItem, -1)}
            basic
            size="small"
            disabled={subLineItem.quantity < 1}
          />
        </Unless>
        <strong>
          &nbsp;
          <FormatNumber value={subLineItem.quantity} />
          &nbsp;
        </strong>
        <Unless condition={subLineItem.isProgressed}>
          <Button
            content="+"
            onClick={this.updateQuantityFn(subLineItem, 1)}
            basic
            size="small"
          />
        </Unless>
      </Unless>
    );
  };

  updateQuantityFn = (subLineItem, modifier) => {
    return () => {
      const { ctrl, userRole } = this.props;
      if (!["admin", "buyer"].includes(userRole)) {
        this.setState({ showAdminMessage: true });
        return;
      }

      const quantity = get(subLineItem, "quantity", 0) + modifier;

      if (quantity >= 0) {
        return ctrl.selectProductsAndQuantities({
          [subLineItem.id]: {
            product_id: get(
              subLineItem,
              ["defactoSelectedOption", "id"],
              subLineItem.product_id
            ),
            quantity
          }
        });
      }
    };
  };

  navigateTo = subLineItem => {
    const { sectionPath, router } = this.props;

    handleScrollTop();
    router.push(
      `${sectionPath}/line_item/${subLineItem.lineItemId}/sub_line_item/${subLineItem.id}`
    );
  };
}

const mapStateToProps = (state, props) => {
  const sectionPath = getSectionPath(props);
  const userRole = getUserRole(state);
  return { sectionPath, userRole };
};

const mapDispatchToProps = (dispatch, props) => {
  return {
    ctrl: roomBookController(dispatch, props.params.id)
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(SubLineItemGrid)
);
